import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { indiceService } from '../services/indiceService.js';
import { 
  Search, 
  ChevronLeft, 
  MapPin, 
  User, 
  SquareUser, 
  Library 
} from 'lucide-react';

const ResultadosBuscador = () => {
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search).get('q');

  useEffect(() => {
    if (query) {
      const fetchResultados = async () => {
        setLoading(true);
        try {
          const response = await indiceService.search(query);
          setResultados(response.data || []);
        } catch (err) {
          console.error('Error al buscar:', err);
          setError('No pudimos conectar con el servidor de índices.');
        } finally {
          setLoading(false);
        }
      };
      fetchResultados();
    }
  }, [query]);

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans pb-20">
      {/* Header Premium - Sintonizado con tu Home Impresionante */}
      <nav className="bg-white shadow-xl border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <span className="text-2xl font-black text-gray-900 tracking-tighter cursor-pointer" onClick={() => navigate('/')}>
                CBR <span className="text-indigo-600 font-black">Digital</span>
              </span>
            </div>
            <div className="flex items-center space-x-6">
               <button onClick={() => navigate('/')} className="text-gray-400 hover:text-indigo-600 text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 transition-all group">
                  <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> Inicio
               </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-2 tracking-tighter uppercase leading-none">Buscador de Índices</h1>
          <p className="text-gray-400 font-medium italic tracking-wide">Mostrando registros para: <span className="font-black text-indigo-600 px-2 uppercase not-italic">"{query}"</span></p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-indigo-600 font-black tracking-widest uppercase text-sm animate-pulse">Sincronizando información...</div>
        ) : error ? (
           <div className="bg-white p-16 rounded-[40px] text-center border border-red-50 text-red-500 font-black uppercase tracking-widest shadow-2xl">
              {error}
           </div>
        ) : resultados.length === 0 ? (
          <div className="bg-white p-20 rounded-[40px] text-center border border-gray-100 shadow-2xl">
            <Search className="h-16 w-16 text-gray-100 mx-auto mb-6" />
            <p className="text-gray-400 text-lg font-medium italic">No se encontraron registros históricos coincidientes.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-10">
            {resultados.map((item) => (
              <div key={item.id} className="bg-white rounded-[40px] shadow-2xl border border-gray-50 overflow-hidden hover:border-indigo-200 transition-all group">
                <div className="p-10">
                  <div className="flex flex-wrap items-center justify-between gap-8 mb-8">
                    <div className="flex items-center gap-6">
                      <div className="bg-indigo-50 p-5 rounded-2xl group-hover:rotate-6 transition-transform">
                        <Library className="h-8 w-8 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-black text-gray-900 tracking-tighter uppercase whitespace-normal">Inscripción #{item.numero}</h3>
                        <p className="text-indigo-400 text-sm font-black uppercase tracking-[0.2em]">Foja {item.foja} | Año {item.anio}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => navigate(`/solicitud-certificado?id=${item.id}`)}
                      className="bg-indigo-600 text-white px-10 py-4 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-indigo-100 active:scale-95"
                    >
                      Solicitar Certificado
                    </button>
                  </div>

                  {/* Detalle Relacional - Diseño Premium */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-gray-50 pt-10">
                    <div>
                      <h4 className="text-[10px] uppercase tracking-[0.3em] font-black text-gray-300 mb-6 flex items-center gap-3">
                        <User className="h-4 w-4" /> Intervinientes Registrados
                      </h4>
                      <div className="flex flex-wrap gap-3 whitespace-normal">
                        {item.intervinientes && item.intervinientes.length > 0 ? item.intervinientes.map((part, pIdx) => (
                          <span key={pIdx} className="inline-flex items-center px-4 py-2 rounded-xl text-[10px] font-black bg-indigo-50/50 text-indigo-700 border border-indigo-100 uppercase tracking-tight">
                            {part.nombre} <span className="ml-2 opacity-50 font-medium">({part.rol})</span>
                          </span>
                        )) : <span className="text-gray-300 text-xs font-medium italic">Protocolo vacío.</span>}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-[10px] uppercase tracking-[0.3em] font-black text-gray-300 mb-6 flex items-center gap-3">
                        <MapPin className="h-4 w-4" /> Datos de Propiedad
                      </h4>
                      <div className="space-y-4 whitespace-normal">
                         {item.inmuebles && item.inmuebles.length > 0 ? item.inmuebles.map((prop, iIdx) => (
                           <div key={iIdx} className="flex items-center gap-3 text-sm text-gray-600 font-bold uppercase tracking-tight">
                             <div className="w-2 h-2 rounded-full bg-indigo-400"></div>
                             {prop.direccion}, {prop.comuna}
                           </div>
                         )) : <span className="text-gray-300 text-xs font-medium italic">Sin datos geográficos.</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="bg-white border-t border-gray-100 py-14 mt-10">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400 text-[10px] font-black uppercase tracking-[0.5em]">
           Información Oficial Registrada - CBR Digital
        </div>
      </footer>
    </div>
  );
};

export default ResultadosBuscador;
